/**
 * Trips API — mirrors `cargoTrips.js` routes. Driver-side trip CRUD plus
 * sender-side `search` + `nearby`.
 */
import { api } from './client';
import type { Trip } from '@/types/trip';

export interface CreateTripInput {
  vehicleId?: number;
  originCity: string;
  originCountry?: string;
  originLat?: number;
  originLng?: number;
  destinationCity: string;
  destinationCountry?: string;
  destinationLat?: number;
  destinationLng?: number;
  departureDate: string;
  estimatedArrivalDate?: string;
  maxWeightKg?: number;
  pricePerKg?: number;
  currency?: string;
  notes?: string;
}

function unwrap<T>(res: { data?: any }): T {
  return (res.data?.data ?? res.data) as T;
}

export const tripsApi = {
  async listMy(): Promise<Trip[]> {
    const r = await api.get(`/api/cargo/trips/my`);
    return unwrap<Trip[]>(r);
  },
  async listActive(): Promise<Trip[]> {
    const r = await api.get(`/api/cargo/trips/active`);
    return unwrap<Trip[]>(r);
  },
  async search(params: {
    originCountry?: string; destinationCountry?: string;
    originCity?: string; destinationCity?: string;
    fromDate?: string; toDate?: string;
  }): Promise<Trip[]> {
    const r = await api.get(`/api/cargo/trips/search`, { params });
    return unwrap<Trip[]>(r);
  },
  async findById(id: number): Promise<Trip> {
    const r = await api.get(`/api/cargo/trips/${id}`);
    return unwrap<Trip>(r);
  },
  async availableShipments(id: number): Promise<unknown[]> {
    const r = await api.get(`/api/cargo/trips/${id}/available-shipments`);
    return unwrap<unknown[]>(r);
  },
  async create(input: CreateTripInput): Promise<Trip> {
    const r = await api.post(`/api/cargo/trips`, input);
    return unwrap<Trip>(r);
  },
  async update(id: number, input: Partial<CreateTripInput>): Promise<Trip> {
    const r = await api.put(`/api/cargo/trips/${id}`, input);
    return unwrap<Trip>(r);
  },
  async cancel(id: number): Promise<void> {
    await api.delete(`/api/cargo/trips/${id}`);
  },
  async updateLocation(id: number, lat: number, lng: number): Promise<void> {
    await api.post(`/api/cargo/trips/${id}/location`, { lat, lng });
  },
};
